import React, { useState } from "react";
import { Typography, Button, List, Upload } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { useChartUpload } from "../../useChartUpload";

export interface ChartAttachment {
  id: string;
  name: string;
  type: string;
  fileLink: string;
  file?: File;
}

interface AttachmentsChartsProps {
  value?: ChartAttachment[];
  onChange?: (charts: ChartAttachment[]) => void;
  disabled?: boolean;
}

export default function AttachmentsCharts({
  value = [],
  onChange,
  disabled = false,
}: AttachmentsChartsProps): React.ReactElement {
  const { uploadMultiple, uploading } = useChartUpload();

  const handleFileSelect = async (fileList: File[]) => {
    if (!fileList.length) return;
    const uploaded = await uploadMultiple(fileList);
    const updated = [...value, ...uploaded];
    onChange?.(updated);
  };

  const handleRemove = (id: string) => {
    const updated = value.filter((c) => c.id !== id);
    onChange?.(updated);
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return "";
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div
      style={{
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: value.length > 0 ? 12 : 0,
        }}
      >
        <div>
          <Typography.Text strong>Attachments (Charts)</Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Supported formats: PDF, XLSX
          </Typography.Text>
        </div>

        <Upload
          accept=".pdf,.xlsx"
          multiple
          showUploadList={false}
          beforeUpload={(file, fileList) => {
            handleFileSelect(fileList);
            return false;
          }}
          disabled={disabled || uploading}
        >
          <Button
            icon={<UploadOutlined />}
            type="primary"
            ghost
            loading={uploading}
            disabled={disabled}
          >
            {uploading ? "Uploading..." : "Upload from Computer"}
          </Button>
        </Upload>
      </div>

      {value.length > 0 && (
        <div
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 16,
            marginTop: 12,
          }}
        >
          <Typography.Text style={{ marginBottom: 12, display: "block" }}>
            {value.length} file{value.length !== 1 ? "s" : ""} attached
          </Typography.Text>

          <List
            dataSource={value}
            renderItem={(item) => (
              <List.Item
                style={{
                  padding: "8px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
                actions={[
                  <Button
                    key="delete"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(item.id)}
                    disabled={disabled}
                    style={{ color: "#999" }}
                  />,
                ]}
              >
                <div>
                  <Typography.Text
                    style={{
                      textDecoration: "underline",
                      color: "#000000D9",
                    }}
                  >
                    {item.name}
                  </Typography.Text>
                  {item.file?.size && (
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 12, marginLeft: 8 }}
                    >
                      ({formatSize(item.file.size)})
                    </Typography.Text>
                  )}
                </div>
              </List.Item>
            )}
          />
        </div>
      )}
    </div>
  );
}
